import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchOnboardingStatus,
  submitForReview,
  createOnboardingProperty,
  createOnboardingUnits,
  configureOnboardingPricing,
  configureOnboardingPolicies,
  completeOnboarding,
  skipOnboarding,
  type OnboardingPropertyData,
  type OnboardingUnitsData,
  type OnboardingPricingData,
  type OnboardingPoliciesData,
} from '@/services/api/onboarding.api';

const ONBOARDING_KEY = ['onboarding', 'status'] as const;

export function useOnboardingStatus(enabled = true) {
  return useQuery({
    queryKey: ONBOARDING_KEY,
    queryFn: fetchOnboardingStatus,
    staleTime: 60_000,
    enabled,
  });
}

export function useCreateOnboardingProperty() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OnboardingPropertyData) => createOnboardingProperty(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });
    },
  });
}

export function useCreateOnboardingUnits() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OnboardingUnitsData) => createOnboardingUnits(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });
    },
  });
}

export function useConfigureOnboardingPricing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OnboardingPricingData) => configureOnboardingPricing(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });
    },
  });
}

export function useConfigureOnboardingPolicies() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: OnboardingPoliciesData) => configureOnboardingPolicies(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });
    },
  });
}

export function useCompleteOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => completeOnboarding(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });
    },
  });
}

export function useSubmitForReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => submitForReview(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });
      void queryClient.invalidateQueries({ queryKey: ['properties'] });
    },
  });
}

export function useSkipOnboarding() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => skipOnboarding(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ONBOARDING_KEY });
    },
  });
}
